// src/components/Footer.js
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {  X } from "lucide-react";

const ScrollLink = ({ to, children, ...props }) => {
  const navigate = useNavigate();

  const handleClick = (e) => {
    window.scrollTo(0, 0);
    if (to) {
      navigate(to);
    }
  };

  return (
    <Link to={to} {...props} onClick={handleClick}>
      {children}
    </Link>
  );
};

const Footer = () => {
  const [email, setEmail] = useState("");
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubscribe = async () => {
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      setSubmitStatus({
        success: false,
        message: "Please enter a valid email",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(
        "https://api.zivbiolabs.com/api/news/email",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus({ success: true, message: "Successfully subscribed!" });
        setEmail("");
      } else {
        setSubmitStatus({
          success: false,
          message: data.error || "Failed to subscribe",
        });
      }
    } catch (error) {
      setSubmitStatus({
        success: false,
        message: "Network error, please try again",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#921a20] box-border max-w-full text-white">
      {/* Newsletter Section */}
      <div className="border-b border-gray-800">
        <div className="max-w-[90%] box-border mx-auto px-4 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center md:text-left"
            >
              <h3
                className="[text-shadow:1px_0_0_#4d1312,2px_0_0_#4d1312,1px_1px_0_#4d1312,2px_2px_0_#4d1312] text-4xl mb-2 text-white"
                style={{
                  fontFamily: "Headlined, sans-serif",
                }}
              >
                Join Our Newsletter
              </h3>
              <p
                className="text-white text-xl"
                style={{
                  fontFamily: "Lobster, sans-serif",
                }}
              >
                Stay updated with our latest offers and products
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="w-full md:w-auto"
            >
              <div className="flex flex-col gap-2 w-full">
                <AnimatePresence mode="wait">
                  {!submitStatus?.success ? (
                    <motion.div
                      key="form"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.4 }}
                      className="flex flex-col sm:flex-row gap-2 w-full"
                    >
                      <motion.input
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        required
                        autoComplete="email"
                        onChange={(e) => setEmail(e.target.value)}
                        className="px-4 py-2 bg-white text-black rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-red/20 w-full sm:w-64"
                        whileFocus={{ scale: 1.02 }}
                        style={{
                          color: "black",
                          WebkitTextFillColor: "black",
                          transition: "background-color 5000s ease-in-out 0s",
                        }}
                        transition={{ type: "spring", stiffness: 300 }}
                      />
                      <motion.button
                        type="button"
                        onClick={handleSubscribe}
                        disabled={isSubmitting}
                        style={{ background: "var(--gradient-secondary)" }}
                        className={`px-6 py-2 rounded-lg text-white font-medium w-full sm:w-auto ${
                          isSubmitting ? "opacity-70 cursor-not-allowed" : ""
                        }`}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        transition={{
                          type: "spring",
                          stiffness: 400,
                          damping: 10,
                        }}
                      >
                        {isSubmitting ? "Subscribing..." : "Subscribe"}
                      </motion.button>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.4 }}
                      className="bg-gradient-to-br from-[#FFD369] via-[#FFB347] to-[#FF8C42] text-black p-6 rounded-xl shadow-xl"
                    >
                      🎉 Thank you! Successfully subscribed.
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Show error/success message if not success yet */}
                <AnimatePresence>
                  {submitStatus && !submitStatus.success && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, height: 0 }}
                      animate={{ opacity: 1, y: 0, height: "auto" }}
                      exit={{ opacity: 0, y: -10, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="flex items-center justify-center md:justify-end gap-2 text-red-200 mt-1"
                    >
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="bg-red-500 rounded-full p-1"
                      >
                        <X className="w-4 h-4 text-white" />
                      </motion.div>
                      <span>{submitStatus.message}</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Footer Main Section */}
      <div className="max-w-[90%] box-border mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="text-center md:text-left">
            <ScrollLink
              to="/"
              className="text-2xl font-bold flex justify-center md:justify-start"
            >
              <img
                src="/media/zibvio-logo-no-bg.svg"
                alt="Product"
                className="w-[220px]"
              />
            </ScrollLink>
            <p className="mt-4 text-white">
              Premium testosterone booster designed for the modern man.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4
              className="tracking-widest text-lg mb-4 ml-0 md:ml-16 text-center md:text-left"
              style={{ fontFamily: "Veneer, sans-serif" }}
            >
              Quick Links
            </h4>
            <ul className="space-y-2 text-center ml-0 md:ml-16 md:text-left">
              <li>
                <ScrollLink
                  to="/shop"
                  className="hover:text-primary-yellow transition-colors"
                >
                  Shop
                </ScrollLink>
              </li>
              <li>
                <ScrollLink
                  to="/faqs"
                  className="hover:text-primary-yellow transition-colors"
                >
                  FAQs
                </ScrollLink>
              </li>
              <li>
                <ScrollLink
                  to="/contact"
                  className="hover:text-primary-yellow transition-colors"
                >
                  Contact
                </ScrollLink>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4
              className="tracking-widest text-lg mb-4 text-center md:text-left"
              style={{ fontFamily: "Veneer, sans-serif" }}
            >
              Support
            </h4>
            <ul className="space-y-2 text-center md:text-left">
              <li>
                <ScrollLink
                  to="/shipping&refund"
                  className="hover:text-primary-yellow transition-colors"
                >
                  Shipping & Refund Policy
                </ScrollLink>
              </li>
              <li>
                <ScrollLink
                  to="/privacy"
                  className="hover:text-primary-yellow transition-colors"
                >
                  Privacy Policy
                </ScrollLink>
              </li>
              <li>
                <ScrollLink
                  to="/terms"
                  className="hover:text-primary-yellow transition-colors"
                >
                  Terms of Service
                </ScrollLink>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4
              className="tracking-widest text-lg mb-4 text-center md:text-left"
              style={{ fontFamily: "Veneer, sans-serif" }}
            >
              Contact Info
            </h4>
            <ul className="space-y-2 text-center md:text-left">
              <li>
                <a
                  href="mailto:contact@zivbiolabs.com"
                  className="hover:text-primary-yellow transition-colors"
                >
                  contact@zivbiolabs.com
                </a>
              </li>
              <li>
                <a
                  href="https://maps.app.goo.gl/4sUSvw8jRCYhFjjDA"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-primary-yellow transition-colors"
                >
                  100 Powell Place #1010 <br />
                  Nashville, TN 37204, USA
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Notice */}
        <div className="mt-8 p-5 border border-white text-center text-sm text-white">
          <p>
            <span
              className=" flex   text-white"
              style={{
                fontFamily: "Helvetica, sans-serif",
                fontSize: "15px",
              }}
            >
              <sup
                style={{
                  top: "0.7em",
                  fontSize: "15px",
                  fontWeight: "bold",
                }}
              >
                †
              </sup>{" "}
              These statements have not been evaluated by the Food and Drug
              Administration. The product on this website is not intended to
              diagnose, treat, cure, or prevent any disease.
            </span>
          </p>
        </div>
        
        <div className="mt-5 pt-3  flex md:hidden flex-col md:flex-row items-center justify-between text-sm text-white">
          <img
            src="/media/made-in-usa_wide.svg"
            alt="Product"
            className="w-[230px]"
          />
        </div>
        {/* Bottom Bar */}
        <div className="mt-5 pt-3 border-t border-gray-800 flex flex-col md:flex-row items-center justify-between text-sm text-white">
          <img
            src="/media/made-in-usa_wide.svg"
            alt="Product"
            className="w-[230px] hidden md:block"
          />
          <p>
            &copy; {new Date().getFullYear()} ZIVBIOLABS. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
